import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';

// @mui
import { styled } from '@mui/material/styles';
import { Link, Container, Typography, Divider, Stack } from '@mui/material';
// hooks
import useResponsive from '../hooks/useResponsive';
// components
import Logo from '../components/logo';
import loginImg from '../assets/illustrations/2 (7).png';
// sections
import { LoginForm } from '../sections/auth/login/index';
import Auth from '../module/Auth';
import region from '../module/region';

// ----------------------------------------------------------------------
const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 500,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 450,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function LoginPage() {
  const [loginFail, setLoginFail] = React.useState(false);

  const navigate = useNavigate();

  const responseGoogle = (response) => {
    const data = jwtDecode(response.credential);

    const loginDataObj = {
      email: data.email,
      name: data.name,
    };
    const login = googleSignIn(loginDataObj);
    if (login === undefined) {
      setLoginFail(true);
    }
  };

  const googleSignIn = async (loginDataObj) => {
    const loginStatus = await Auth.authenticateGmail(loginDataObj, (response) => {
      if (response.status === 'success') {
        region.getRegions();

        const userId = Auth.isAuthenticated()?.user?.id; // Get the user ID from the authenticated user object
        const isSubscribed = Auth.subscribed(userId); // Pass the user ID to the subscribed function
        if (isSubscribed) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/subscription', { replace: true });
        }
      }
    });
    return loginStatus;
  };

  const mdUp = useResponsive('up', 'md');

  return (
    <>
      <Helmet>
        <title> Login | ML-Rank Tracker </title>
      </Helmet>

      <StyledRoot>
        <Logo
          sx={{
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
          }}
        />

        {mdUp && (
          <StyledSection>
            <img src={loginImg} alt="login" />
          </StyledSection>
        )}

        <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" gutterBottom>
              Sign in to Google Rank Check
            </Typography>
            <span
              style={{
                display: 'flex',
                justifyContent: 'end',
                fontSize: '14px',
                marginTop: '-10px',
              }}
            >
              Powered by Marketing Lad
            </span>

            <Typography variant="subtitle" sx={{ mb: 5 }}>
              Create Account {''}
              <Link
                onClick={() => {
                  navigate('/signup', { replace: true });
                }}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
              >
                Signup
              </Link>
            </Typography>
            <Stack direction="row" spacing={2} display="flex" alignItems="center" justifyContent="center">
              <GoogleLogin
                onSuccess={responseGoogle}
                onFailure={responseGoogle}
                cookiePolicy={'single_host_origin'}
                variant="outlined"
                className="google-auth"
                style={{ display: 'block', height: '100%' }}
              />
            </Stack>
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                OR
              </Typography>
            </Divider>
            <LoginForm />
            <span
              style={{
                marginTop: '5px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="subtitle2">
                {' '}
                <Link href="privacy-policy"> Privacy Policy!</Link>
              </Typography>
              <Typography variant="subtitle2">
                {' '}
                <Link href="support"> Contact us!</Link>
              </Typography>
              <Typography variant="subtitle2">
                {' '}
                <Link href="terms-of-use"> Terms & Conditions!</Link>
              </Typography>
            </span>
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}
